import { useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { GET_ETS_QUIZ_QUESTIONS } from '../../apollo/queries/ets';
import { SUBMIT_ETS_ATTEMPT } from '../../apollo/mutations/ets';
import {
  setETSQuizQuestions,
  selectETSAnswer,
  goToNextETSQuestion,
  goToPreviousETSQuestion,
  goToSpecificETSQuestion,
  setETSQuizResults,
} from '../../redux/slices/etsQuizSlice';
import { addAssessment as addAthleteAssessment } from '../../redux/slices/athleteSlice';
import { addAssessment as addCoachAssessment } from '../../redux/slices/coachSlice';
import BackButton from '../../components/BackButton/BackButton';
import QuestionNavigationPanel from '../../components/QuestionNavigationPanel/QuestionNavigationPanel';
import QuizQuestion from '../../components/QuizQuestion/QuizQuestion';
import styles from '../QuizDashboard/QuizDashboard.module.css';

const ETSQuizDashboard = () => {
  const { questions, answers, currentQuestionIndex, isSubmitted, gScore, etsTraitScores } = useSelector(state => state.etsQuiz);
  const role = useSelector(state => state.auth.role);
  const dispatch = useDispatch();

  const { data, loading, error } = useQuery(GET_ETS_QUIZ_QUESTIONS);
  const [submitETSAttempt] = useMutation(SUBMIT_ETS_ATTEMPT);

  useEffect(() => {
    if (data?.getETSQuizQuestions) {
      dispatch(setETSQuizQuestions(data.getETSQuizQuestions));
    }
  }, [data, dispatch]);

  if (loading) return <p>Loading ETS Quiz...</p>;
  if (error) return <p>Error loading questions.</p>;
  if (!questions.length) return <p>No ETS Questions Available.</p>;

  const currentQuestionRaw = questions[currentQuestionIndex];

  const currentQuestion = {
    questionText: currentQuestionRaw.prompt,
    options: [1, 2, 3, 4, 5].map((num, idx) => ({
      label: String.fromCharCode(65 + idx),
      text: String(num)
    }))
  };

  const handleAnswerSelect = (selectedOptionText) => {
    dispatch(selectETSAnswer({
      questionId: currentQuestionRaw._id,
      selectedValue: Number(selectedOptionText)
    }));
  };

  const handleSubmit = async () => {
    try {
      const formattedAnswers = answers.filter(a => a !== null);
      const { data } = await submitETSAttempt({
        variables: { input: { answers: formattedAnswers } }
      });
  
      const attempt = data.submitETSAttempt;
  
      dispatch(setETSQuizResults({
        gScore: attempt.gScore,
        etsTraitScores: attempt.etsTraitScores,
      }));
  
      if (role === 'athlete') {
        dispatch(addAthleteAssessment(attempt));
      } else if (role === 'coach') {
        dispatch(addCoachAssessment(attempt));
      }
    } catch (err) {
      console.error('Failed to submit ETS quiz:', err);
    }
  };

  if (isSubmitted) {
    return (
      <div className={styles.dashboardWrapper}>
        <h1 className={styles.title}>Personality Quiz</h1>
        <BackButton to="/dashboard" />
  
        <div className={styles.etsResultsWrapper}>
          <div className={styles.etsResultsCard}>
            <h2 className={styles.resultsTitle}>G-Score: {gScore}</h2>
            <div className={styles.traitGrid}>
              {Object.entries(etsTraitScores)
                .filter(([key]) => key !== '__typename')
                .map(([trait, score]) => (
                  <div key={trait} className={styles.traitCard}>
                    <p className={styles.traitName}>{trait}</p>
                    <p className={styles.traitScore}>{score}</p>
                  </div>
                ))}
            </div>
            <p className={styles.feedbackMessage}>
              {gScore >= 7.5
                ? 'Excellent emotional traits — well balanced and competitive!'
                : gScore >= 6
                ? 'Good job! Keep growing your mindset!'
                : 'Consider practicing discipline and focus regularly to boost your traits.'}
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={styles.dashboardWrapper}>
      <h1 className={styles.title}>Personality Quiz</h1>
      <BackButton to="/dashboard" />
      
      <QuestionNavigationPanel
        questions={questions}
        selectedAnswers={answers.map((a) => a?.selectedValue ?? null)}
        currentIndex={currentQuestionIndex}
        onQuestionSelect={(index) => dispatch(goToSpecificETSQuestion(index))}
        isAnsweredFn={(q, i) => answers[i] !== null}
      />

      <QuizQuestion
        question={currentQuestion}
        currentIndex={currentQuestionIndex}
        totalQuestions={questions.length}
        onAnswerSelect={handleAnswerSelect}
        selectedAnswer={answers[currentQuestionIndex]?.selectedValue}
        isSubmitted={isSubmitted}
        isScaleMode={true}
      />

      <div className={styles.navigationButtons}>
        <button onClick={() => dispatch(goToPreviousETSQuestion())} disabled={currentQuestionIndex === 0}>
          Previous
        </button>
        {currentQuestionIndex < questions.length - 1 ? (
          <button onClick={() => dispatch(goToNextETSQuestion())}>
            Next
          </button>
        ) : (
          <button onClick={handleSubmit}>
            Submit Quiz
          </button>
        )}
      </div>
    </div>
  );
};

export default ETSQuizDashboard;